//
//  FitinsightUserTable.tsx
//
//  isana.net,inc. Proprietary and Confidential
//

import React, { useCallback, useState } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
} from "@mui/material";
import {
  BLOOD_PRESSURE_USED,
  FITBIT_CLIENT_ID,
  FITINSIGHT_UNIQUE_ID_MESSAGE,
} from "../utils/env";
import { FilterList } from "@mui/icons-material";
import FilterDialog from "./FilterDialog";

interface FitinsightUserTableProps {
  allFilters: string[];
  checkedFilters: string[];
  users: FitinsightUserTableRow[];
  userCount: number;
  rowsPerPage: number;
  page: number;
  checkedAll: boolean;
  hadlePageChange: (page: number) => void;
  handleChecked: (name: string) => void;
}

export type FitinsightUserTableRow = {
  companyId: string;
  fitinsightUniqueId: string;
  latestFetchDate: string | undefined;
  // [FITBIT-MIGRATION] 現在の連携方式（googleHealth / fitbit）。GoogleHealth移行状況列の
  // 表示用（移行済み=緑 / 未移行=赤）。Fitbit廃止時に削除可（E3-4）。
  integrationType?: "googleHealth" | "fitbit";
  rowType: "normal" | "alert";
};

const TABLE_SIZE = 650;
const BACKGROUND_COLOR = "#b7b7b9db";
const ALERT_COLOR = "#ff0000";
// [FITBIT-MIGRATION] GHA移行状況の「移行済み」表示に使う緑。Fitbit廃止時に削除可（E3-4）。
const MIGRATED_COLOR = "#2e7d32";

const FitinsightUserTable: React.FC<FitinsightUserTableProps> = ({
  allFilters,
  checkedFilters,
  users,
  userCount,
  rowsPerPage,
  page,
  checkedAll,
  hadlePageChange,
  handleChecked,
}) => {
  const [filterOpen, setFilterOpen] = useState<boolean>(false);

  const renderHeaderCell = useCallback(
    (label: string, align: "center" | "left" | "right" = "center") => (
      <TableCell align={align}>{label}</TableCell>
    ),
    []
  );

  const renderDataCell = useCallback(
    (
      content: string | undefined,
      align: "center" | "left" | "right" = "center",
      style = {}
    ) => (
      <TableCell align={align} style={style}>
        {content ?? ""}
      </TableCell>
    ),
    []
  );

  return (
    <>
      <TableContainer
        component={Paper}
        sx={{ maxWidth: TABLE_SIZE, border: 1, borderColor: "gray" }}
      >
        <Table
          sx={{
            minWidth: TABLE_SIZE,
            maxWidth: TABLE_SIZE,
            margin: "auto",
          }}
          size="small"
          aria-label="a dense table"
        >
          <TableHead sx={{ backgroundColor: BACKGROUND_COLOR }}>
            <TableRow>
              {
                // BLOOD_PRESSURE_USEDがtrue=companyIdの利用があると判断
                // 別途環境変数の定義が必要かは要検討
              }
              {BLOOD_PRESSURE_USED && (
                <TableCell align="center">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    Company ID
                    <IconButton
                      size="small"
                      onClick={() => setFilterOpen(true)}
                    >
                      <FilterList />
                    </IconButton>
                  </div>
                </TableCell>
              )}
              {renderHeaderCell(FITINSIGHT_UNIQUE_ID_MESSAGE ?? "")}
              {
                // FITBIT_CLIENT_IDが空でない=Fitbitの利用があると判断
                // 別途環境変数の定義が必要かは要検討
              }
              {FITBIT_CLIENT_ID !== "" && renderHeaderCell("最新データ取得日")}
              {
                // [FITBIT-MIGRATION] Fitbit→GHA移行期の暫定列（E3-4）。移行完了後は
                // このヘッダ・データセル・integrationType 一式を削除する
                // （grep "FITBIT-MIGRATION" で関連箇所を一括で見つけられる）。
              }
              {renderHeaderCell("GoogleHealth移行状況")}
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user: FitinsightUserTableRow) => (
              <TableRow key={user.fitinsightUniqueId + user.companyId}>
                {BLOOD_PRESSURE_USED &&
                  renderDataCell(user.companyId, "center")}
                {renderDataCell(user.fitinsightUniqueId, "center")}
                {FITBIT_CLIENT_ID !== "" &&
                  renderDataCell(user.latestFetchDate ?? "未取得", "center", {
                    color: user.rowType === "alert" ? ALERT_COLOR : "inherit",
                  })}
                {
                  // [FITBIT-MIGRATION] GoogleHealth移行状況（E3-4・暫定列）。
                  // 移行済み=緑/未移行=赤。Fitbit廃止時にこのセル・ヘッダ・
                  // integrationType 一式を削除する（grep "FITBIT-MIGRATION"）。
                }
                <TableCell align="center">
                  {user.integrationType === "googleHealth" && (
                    <Chip
                      label="移行済み"
                      size="small"
                      variant="outlined"
                      sx={{
                        height: 20,
                        fontSize: "0.7rem",
                        color: MIGRATED_COLOR,
                        borderColor: MIGRATED_COLOR,
                      }}
                    />
                  )}
                  {user.integrationType === "fitbit" && (
                    <Chip
                      label="未移行"
                      size="small"
                      variant="outlined"
                      sx={{
                        height: 20,
                        fontSize: "0.7rem",
                        color: ALERT_COLOR,
                        borderColor: ALERT_COLOR,
                      }}
                    />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={userCount}
          rowsPerPageOptions={[]}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => {
            hadlePageChange(newPage);
          }}
        />
      </TableContainer>
      <FilterDialog
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        allFilters={allFilters}
        checkedFilters={checkedFilters}
        checkedAll={checkedAll}
        handleChecked={handleChecked}
      />
    </>
  );
};

export default FitinsightUserTable;
